import { Button } from "@/components/ui/Button";
import {
  useDatasetMonitors,
  useDeleteDatasetMonitor,
  useRunDatasetMonitor,
  useSetDatasetMonitorEnabled,
} from "@/features/data-curation/hooks/useDatasetMonitors";
import type { DatasetMonitor, DatasetMonitorRunStatus } from "@/features/data-curation/types";
import { CalendarClock, Pause, Play, Trash2, Zap } from "lucide-react";
import { useId } from "react";
import { toast } from "sonner";

const formatWhen = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const runOutcome: Record<DatasetMonitorRunStatus, string> = {
  queued: "waiting to start",
  running: "running",
  completed: "done",
  failed: "failed",
  skipped: "skipped",
  stopped: "stopped at the time limit",
};

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "Unknown error";

const MonitorItem = ({
  datasetId,
  monitor,
}: {
  datasetId: string;
  monitor: DatasetMonitor;
}) => {
  const setEnabled = useSetDatasetMonitorEnabled(datasetId);
  const remove = useDeleteDatasetMonitor(datasetId);
  const run = useRunDatasetMonitor(datasetId);
  const busy = setEnabled.isPending || remove.isPending || run.isPending;
  const lastProblem =
    monitor.last_error && monitor.last_status !== "completed" ? monitor.last_error : null;

  const runNow = () =>
    run.mutate(monitor.id, {
      onSuccess: () =>
        toast.success(`“${monitor.name}” runs when the agent is free. You can follow it in the chat.`),
      onError: (error) => toast.error(`Could not run “${monitor.name}”: ${errorText(error)}`),
    });
  const toggle = () =>
    setEnabled.mutate(
      { monitorId: monitor.id, enabled: !monitor.enabled },
      {
        onError: (error) =>
          toast.error(`Could not change “${monitor.name}”: ${errorText(error)}`),
      },
    );
  const deleteMonitor = () => {
    if (
      !window.confirm(
        `Delete the monitor “${monitor.name}”? Its past runs stay in the chat.`,
      )
    ) {
      return;
    }
    remove.mutate(monitor.id, {
      onError: (error) => toast.error(`Could not delete “${monitor.name}”: ${errorText(error)}`),
    });
  };

  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-2">
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium text-zinc-900">{monitor.name}</p>
        <p className="text-xs text-zinc-600">
          {monitor.schedule}
          {" · "}
          {monitor.running ? (
            <span className="inline-flex items-center gap-1 font-medium text-blue-700">
              <span
                className="h-1.5 w-1.5 rounded-full bg-blue-600 motion-safe:animate-pulse"
                aria-hidden
              />
              Running now
            </span>
          ) : monitor.enabled && monitor.next_run_at ? (
            `Next run ${formatWhen(monitor.next_run_at)}`
          ) : (
            "Paused"
          )}
        </p>
        {monitor.last_run_at && monitor.last_status && (
          <p className="text-xs text-zinc-500">
            Last run {formatWhen(monitor.last_run_at)}: {runOutcome[monitor.last_status]}
            {lastProblem ? `. ${lastProblem}` : ""}
          </p>
        )}
        <details className="text-xs text-zinc-600">
          <summary className="cursor-pointer text-zinc-500 hover:text-zinc-800">
            Prompt
          </summary>
          <p className="mt-1 whitespace-pre-wrap rounded-[3px] bg-zinc-50 px-2 py-1.5 text-zinc-700">
            {monitor.prompt}
          </p>
        </details>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button
          size="sm"
          variant="outline"
          disabled={busy || monitor.running}
          loading={run.isPending}
          title="Run the monitor now"
          onClick={runNow}
        >
          <Zap className="h-3.5 w-3.5" aria-hidden />
          Run now
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          loading={setEnabled.isPending}
          onClick={toggle}
        >
          {monitor.enabled ? (
            <Pause className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Play className="h-3.5 w-3.5" aria-hidden />
          )}
          {monitor.enabled ? "Pause" : "Resume"}
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={busy}
          aria-label={`Delete the monitor ${monitor.name}`}
          title="Delete monitor"
          className="text-zinc-500 hover:bg-red-50 hover:text-red-700"
          onClick={deleteMonitor}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </li>
  );
};

/** The dataset's monitors: saved prompts that its chat gets on a schedule. Hidden while it has none. */
export const MonitorsPanel = ({ datasetId }: { datasetId: string }) => {
  const headingId = useId();
  const monitorsQuery = useDatasetMonitors(datasetId, true);
  const monitors = monitorsQuery.data ?? [];
  if (monitors.length === 0) return null;
  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[3px] border border-zinc-200 bg-white px-3 py-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2
          id={headingId}
          className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900"
        >
          <CalendarClock className="h-4 w-4 text-zinc-500" aria-hidden />
          Monitors
        </h2>
        <p className="text-xs text-zinc-500">
          Each monitor updates the dataset on its schedule. A checkpoint is saved before
          each run, and the chat shows what each run did.
        </p>
      </div>
      <ul className="divide-y divide-zinc-100">
        {monitors.map((monitor) => (
          <MonitorItem key={monitor.id} datasetId={datasetId} monitor={monitor} />
        ))}
      </ul>
    </section>
  );
};
